import { Alert, Badge, List, Stack, Table, Text } from '@mantine/core'
import { IconCalendar, IconCalendarOff } from '@tabler/icons-react'
import { SectionCard } from '@lighthouse/design'
import { CartographerLink } from '../cartographer/CartographerLink'
import { useReportDetailScheduling } from '../../hooks/useReportDetail'
import { NOT_MEASURED, whenLabel } from '../../utils/format'
import type { ReportDetailSchedule, ReportDetailScheduling } from '../../types/reportDetail'
import { Caption, EvaluatedAt, TabFailure, TabLoading } from './OverviewTab'
import {
  nextOccurrenceReasonLabel,
  notDeliveringText,
  recipientSlotsText,
  runAsText,
  scheduleErrorLabel,
  scheduleOutcomeLabel,
  scheduleOutcomeTone,
  showingText,
  toneColor,
} from './reportDetailFormat'

/**
 * Report Detail → Scheduling: every schedule of this report, its last outcome
 * BY NAME (never painted as good — a last delivery says nothing about the next,
 * and nothing records a recipient reading it), why its last run failed as a
 * category (never the stored text), and its next firings — or why there are none.
 *
 * Read-only (D6a): pausing, editing and deleting a schedule are Town Crier's, in
 * Cartographer, and the hand-off is a link.
 */
export function SchedulingTab({ reportId }: { reportId: string }) {
  const { data, isLoading, isError, error } = useReportDetailScheduling(reportId, { open: true })

  if (isError) return <TabFailure error={error} what="this report’s schedules" />
  if (isLoading || !data) return <TabLoading />

  return <SchedulingBody scheduling={data} />
}

function SchedulingBody({ scheduling }: { scheduling: ReportDetailScheduling }) {
  const notDelivering = notDeliveringText({ status: scheduling.reportStatus, isRunnable: scheduling.isRunnable })
  const capped = scheduling.schedules.length < scheduling.scheduleCount

  return (
    <Stack gap="lg">
      <Alert
        variant="light"
        color={scheduling.isRunnable ? 'gray' : 'yellow'}
        icon={scheduling.isRunnable ? <IconCalendar size={16} /> : <IconCalendarOff size={16} />}
        title={notDelivering ? `Every schedule ${notDelivering}` : `Report is ${scheduling.reportStatus}`}
      >
        <Text size="sm">{scheduling.statusNote}</Text>
      </Alert>

      <SectionCard
        title="Schedules"
        subtitle={capped
          ? `${showingText(scheduling.schedules.length, scheduling.scheduleCount)} — active first, then by name`
          : 'Active first, then by name'}
        action={<CartographerLink path="/schedules" label="Schedules">Town Crier, in Cartographer</CartographerLink>}
      >
        {scheduling.schedules.length === 0 ? (
          <Text size="sm" c="dimmed">This report has no schedules.</Text>
        ) : (
          <Stack gap="xs">
            {scheduling.runAsWithheldCaption && <Caption>{scheduling.runAsWithheldCaption}</Caption>}
            <ScheduleTable schedules={scheduling.schedules} />
          </Stack>
        )}
      </SectionCard>

      <SectionCard title="Not visible here">
        <List size="sm" spacing={4}>
          {scheduling.notVisibleHere.map((line) => <List.Item key={line}>{line}</List.Item>)}
        </List>
      </SectionCard>

      <EvaluatedAt at={scheduling.evaluatedAt} />
    </Stack>
  )
}

function ScheduleTable({ schedules }: { schedules: ReportDetailSchedule[] }) {
  return (
    <Table.ScrollContainer minWidth={980}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Schedule</Table.Th>
            <Table.Th>When</Table.Th>
            <Table.Th>Runs as</Table.Th>
            <Table.Th>Recipients</Table.Th>
            <Table.Th>Last run</Table.Th>
            <Table.Th>Next firings (your time)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {schedules.map((schedule) => <ScheduleRow key={schedule.scheduleId} schedule={schedule} />)}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function ScheduleRow({ schedule }: { schedule: ReportDetailSchedule }) {
  const errorLabel = scheduleErrorLabel(schedule.lastRunErrorCategory)
  const owner = schedule.runAsIsOwner === true ? 'the owner'
    : schedule.runAsIsOwner === false ? 'not the owner'
      : 'owner unknown'

  return (
    <Table.Tr data-schedule={schedule.scheduleId}>
      <Table.Td>
        <Text size="sm" fw={500}>{schedule.name}</Text>
        <Badge size="xs" variant="light" color="gray">{schedule.isActive ? 'Active' : 'Paused'}</Badge>
        {schedule.hasParameterOverrides && <Caption>stores parameter values of its own</Caption>}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{schedule.cronDescription}</Text>
        <Caption>{schedule.cronExpression} · {schedule.timeZoneId}</Caption>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{runAsText(schedule)}</Text>
        <Caption>{owner}</Caption>
      </Table.Td>
      <Table.Td>{recipientSlotsText(schedule.recipientSlots)}</Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Badge
            variant="light"
            tt="none"
            color={toneColor(scheduleOutcomeTone(schedule.lastOutcome))}
            data-outcome={schedule.lastOutcome ?? 'none'}
          >
            {scheduleOutcomeLabel(schedule.lastOutcome)}
          </Badge>
          {errorLabel && <Text size="xs">{errorLabel}</Text>}
          <Caption>{whenLabel(schedule.lastRunAt)}</Caption>
        </Stack>
      </Table.Td>
      <Table.Td>
        {schedule.nextOccurrences.length > 0 ? (
          <Stack gap={0}>
            {schedule.nextOccurrences.map((at) => <Text key={at} size="xs">{whenLabel(at)}</Text>)}
          </Stack>
        ) : (
          <Stack gap={0}>
            <Text size="sm">{NOT_MEASURED}</Text>
            <Caption>{nextOccurrenceReasonLabel(schedule.nextOccurrencesUnavailableReason)}</Caption>
          </Stack>
        )}
      </Table.Td>
    </Table.Tr>
  )
}
